import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SMSSendModal from './SMSSendModal';

import { useState, useEffect } from 'react';

const AttendeeDetailsModal = ({ isOpen, onClose, attendee, onCheckIn, onProcessRefund }) => {
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Fetch booking data when attendee changes
  useEffect(() => {
    const fetchBookingData = async () => {
      if (attendee?.ticketId && isOpen) {
        setLoading(true);
        try {
          // In a real app, you'd fetch the complete booking data
          // For now, we'll simulate it based on the attendee data
          const mockBooking = {
            id: attendee?.id,
            booking_reference: attendee?.ticketId,
            event: {
              title: 'Tech Conference 2025', // This would come from your event data
              event_date: '2025-12-15T09:00:00Z',
              venue_name: 'SF Convention Center',
              location: 'San Francisco Convention Center'
            },
            ticket: {
              ticket_name: attendee?.ticketTier,
              price: attendee?.amount,
              ticket_type: attendee?.ticketTier?.toLowerCase()
            },
            quantity: 1,
            total_amount: attendee?.amount,
            booking_status: attendee?.checkInStatus === 'checked-in' ? 'confirmed' : 'confirmed',
            attendee: {
              full_name: attendee?.name,
              email: attendee?.email
            }
          };
          setBookingData(mockBooking);
        } catch (error) {
          console.error('Error fetching booking:', error);
        }
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [attendee, isOpen]);

  const handleSMSSuccess = (message) => {
    setToastMessage({ message, type: 'success' });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleSMSError = (message) => {
    setToastMessage({ message, type: 'error' });
    setTimeout(() => setToastMessage(null), 3000);
  };

  if (!isOpen || !attendee) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      'checked-in': { color: 'bg-success text-success-foreground', label: 'Checked In', icon: 'CheckCircle' },
      'not-checked-in': { color: 'bg-muted text-muted-foreground', label: 'Not Checked In', icon: 'Clock' },
      'late': { color: 'bg-warning text-warning-foreground', label: 'Late Arrival', icon: 'AlertTriangle' }
    };

    const config = statusConfig?.[status] || statusConfig?.['not-checked-in'];
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={14} className="mr-1" />
        {config?.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': { color: 'bg-success text-success-foreground', label: 'Paid', icon: 'CheckCircle' },
      'pending': { color: 'bg-warning text-warning-foreground', label: 'Pending', icon: 'Clock' },
      'refunded': { color: 'bg-muted text-muted-foreground', label: 'Refunded', icon: 'RotateCcw' }
    };

    const config = statusConfig?.[status] || statusConfig?.['pending'];
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={14} className="mr-1" />
        {config?.label}
      </span>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="User" size={24} className="text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Attendee Details</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Check-in Status</label>
                <div>{getStatusBadge(attendee?.checkInStatus)}</div>
                {attendee?.checkInTime && (
                  <p className="text-sm text-muted-foreground">
                    Checked in at {new Date(attendee.checkInTime)?.toLocaleString()}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Payment Status</label>
                <div>{getPaymentStatusBadge(attendee?.paymentStatus)}</div>
              </div>
            </div>

            {/* Ticket Information */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-foreground flex items-center">
                <Icon name="Ticket" size={16} className="mr-2" />
                Ticket Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Ticket ID</label>
                  <p className="text-sm text-foreground font-mono">{attendee?.ticketId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Ticket Tier</label>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-3 h-3 rounded-full ${
                      attendee?.ticketTier === 'VIP' ? 'bg-yellow-500' :
                      attendee?.ticketTier === 'Premium' ? 'bg-blue-500' : 'bg-gray-500'
                    }`} />
                    <span className="text-sm font-medium text-foreground">{attendee?.ticketTier}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Purchase Date</label>
                  <p className="text-sm text-foreground">
                    {new Date(attendee.purchaseDate)?.toLocaleDateString()} at{' '}
                    {new Date(attendee.purchaseDate)?.toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Amount Paid</label>
                  <p className="text-sm text-foreground font-medium">${attendee?.amount}</p>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-foreground flex items-center">
                <Icon name="CreditCard" size={16} className="mr-2" />
                Payment Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Payment Method</label>
                  <p className="text-sm text-foreground">{attendee?.paymentMethod}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Transaction ID</label>
                  <p className="text-sm text-foreground font-mono">{attendee?.transactionId}</p>
                </div>
                {attendee?.discountCode && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Discount Code</label>
                    <p className="text-sm text-foreground">{attendee?.discountCode}</p>
                  </div>
                )}
                {attendee?.discountAmount && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Discount Applied</label>
                    <p className="text-sm text-success">-${attendee?.discountAmount}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-foreground flex items-center">
                <Icon name="User" size={16} className="mr-2" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <p className="text-sm text-foreground">{attendee?.email}</p>
                </div>
                {attendee?.phone && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <p className="text-sm text-foreground">{attendee?.phone}</p>
                  </div>
                )}
                {attendee?.company && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Company</label>
                    <p className="text-sm text-foreground">{attendee?.company}</p>
                  </div>
                )}
                {attendee?.dietaryRestrictions && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Dietary Restrictions</label>
                    <p className="text-sm text-foreground">{attendee?.dietaryRestrictions}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Special Notes */}
            {attendee?.specialNotes && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-medium text-foreground flex items-center mb-2">
                  <Icon name="FileText" size={16} className="mr-2" />
                  Special Notes
                </h3>
                <p className="text-sm text-foreground">{attendee?.specialNotes}</p>
              </div>
            )}

            {/* QR Code */}
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <h3 className="font-medium text-foreground flex items-center justify-center mb-4">
                <Icon name="QrCode" size={16} className="mr-2" />
                Ticket QR Code
              </h3>
              <div className="w-32 h-32 bg-white border border-border rounded-lg mx-auto flex items-center justify-center">
                <Icon name="QrCode" size={48} className="text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Scan this code for quick check-in
              </p>
            </div>

            {/* Actions */}
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-3 flex items-center">
                <Icon name="Settings" size={16} className="mr-2" />
                Quick Actions
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {attendee?.checkInStatus !== 'checked-in' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onCheckIn?.(attendee?.id)}
                    iconName="CheckCircle"
                    iconPosition="left"
                  >
                    Check In
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSMSModal(true)}
                  iconName="MessageSquare"
                  iconPosition="left"
                  disabled={!attendee?.phone}
                >
                  Send SMS
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  iconName="Mail"
                  iconPosition="left"
                >
                  Send Email
                </Button>

                {attendee?.paymentStatus === 'paid' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onProcessRefund?.(attendee?.id)}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Process Refund
                  </Button>
                )}
              </div>

              {!attendee?.phone && (
                <p className="text-xs text-muted-foreground mt-2">
                  SMS unavailable - no phone number on file
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Last updated: {new Date(attendee?.purchaseDate)?.toLocaleString()}
            </div>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* SMS Modal */}
      <SMSSendModal
        isOpen={showSMSModal}
        onClose={() => setShowSMSModal(false)}
        booking={bookingData}
        attendee={attendee}
        onSuccess={handleSMSSuccess}
        onError={handleSMSError}
      />

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <div className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg ${
            toastMessage?.type === 'success' ?'bg-success text-success-foreground' :'bg-error text-error-foreground'
          }`}>
            <Icon 
              name={toastMessage?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
            />
            <span className="text-sm font-medium">{toastMessage?.message}</span>
            <button
              onClick={() => setToastMessage(null)}
              className="hover:opacity-80"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AttendeeDetailsModal;